import { useMutation } from '@tanstack/react-query'
import { Bot, Send, User } from 'lucide-react'
import type { SubmitEvent } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Alert, Button, Input, SpinnerIcon } from '@/components/ui'
import { cn } from '@/lib/utils'

import { runPlayground } from '../api'
import type { PlaygroundResult } from '../types'

/** Sugestoes de exemplo do estado vazio (empty state = action state, docs/design/01 secao 7). */
const SUGGESTION_KEYS = [
  'onboarding.playground.suggestions.first',
  'onboarding.playground.suggestions.second',
  'onboarding.playground.suggestions.third',
] as const

interface ChatTurn {
  readonly id: number
  readonly role: 'user' | 'agent'
  readonly text: string
}

export interface AgentPlaygroundProps {
  readonly tenantId: string
  readonly agentId: string
}

/**
 * Playground por agente: o usuario conversa com o agente pelo endpoint de Playground (nunca envia
 * nada a clientes reais). Reutiliza a experiencia e os textos do passo de onboarding, sem o
 * acoplamento da wizard (avanco de estagio, CTA de continuar). Vocabulario neutro; sem "AI theater".
 */
export function AgentPlayground({ tenantId, agentId }: AgentPlaygroundProps) {
  const { t } = useTranslation()

  const [turns, setTurns] = useState<readonly ChatTurn[]>([])
  const [draft, setDraft] = useState('')
  const nextTurnId = useRef(0)

  const mutation = useMutation({
    mutationFn: (variables: { message: string; userTurnId: number }): Promise<PlaygroundResult> =>
      runPlayground(tenantId, agentId, variables.message),
    onSuccess: (result) => {
      // A fala do usuario ja foi ecoada otimisticamente em `send`; aqui so anexamos a resposta do agente.
      const reply = result.decision.responseText ?? t('onboarding.playground.handoffNote')
      setTurns((current) => [...current, { id: nextTurnId.current++, role: 'agent', text: reply }])
    },
    onError: (_error, variables) => {
      // Falha (inclui provider indisponivel): desfaz o eco otimista e devolve o texto ao input para reenviar.
      // So restaura se o campo estiver vazio: se o usuario ja comecou a digitar a proxima mensagem (type-ahead
      // durante o loading), NAO sobrescrevemos o que ele digitou.
      setTurns((current) => current.filter((turn) => turn.id !== variables.userTurnId))
      setDraft((current) => (current.trim().length === 0 ? variables.message : current))
    },
  })

  function send(message: string): void {
    const trimmed = message.trim()
    if (trimmed.length === 0 || mutation.isPending) {
      return
    }

    // Eco otimista: a fala do usuario aparece imediatamente (loading distinto de "sem resposta").
    const userTurnId = nextTurnId.current++
    setTurns((current) => [...current, { id: userTurnId, role: 'user', text: trimmed }])
    setDraft('')
    mutation.mutate({ message: trimmed, userTurnId })
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    send(draft)
  }

  const providerUnavailable =
    mutation.error instanceof ApiError &&
    mutation.error.problem.errorCode === 'ai_provider_not_configured'

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-foreground-muted">{t('onboarding.playground.disclaimer')}</p>

      {providerUnavailable ? (
        <Alert variant="warning" title={t('onboarding.playground.providerUnavailableTitle')}>
          {t('onboarding.playground.providerUnavailableDescription')}
        </Alert>
      ) : mutation.isError ? (
        <Alert variant="danger" title={t('onboarding.playground.errorTitle')}>
          {t('onboarding.playground.errorDescription')}
        </Alert>
      ) : null}

      <div className="flex min-h-[200px] flex-col gap-3 rounded-xl border border-border-default bg-background-subtle p-4">
        {turns.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-6 text-center">
            <span
              aria-hidden="true"
              className="flex h-11 w-11 items-center justify-center rounded-2xl bg-background-elevated text-action-primary"
            >
              <Bot className="h-5 w-5" />
            </span>
            <p className="text-sm font-medium text-foreground-primary">
              {t('onboarding.playground.emptyTitle')}
            </p>
            <p className="max-w-sm text-xs text-foreground-muted">
              {t('onboarding.playground.emptyHint')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
              {SUGGESTION_KEYS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    send(t(key))
                  }}
                  disabled={mutation.isPending}
                  className="rounded-full border border-border-default bg-background-elevated px-3 py-1.5 text-xs text-foreground-secondary transition-colors hover:border-action-primary hover:text-action-primary disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t(key)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ol className="flex flex-col gap-3">
            {turns.map((turn) => (
              <li
                key={turn.id}
                className={cn(
                  'flex gap-2.5',
                  turn.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                    turn.role === 'user'
                      ? 'bg-action-primary text-foreground-on-action'
                      : 'bg-background-elevated text-action-primary',
                  )}
                >
                  {turn.role === 'user' ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </span>
                <div
                  className={cn(
                    'max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
                    turn.role === 'user'
                      ? 'bg-action-primary text-foreground-on-action'
                      : 'bg-background-elevated text-foreground-primary',
                  )}
                >
                  <span className="sr-only">
                    {turn.role === 'user'
                      ? t('onboarding.playground.youLabel')
                      : t('onboarding.playground.agentLabel')}
                    :{' '}
                  </span>
                  {turn.text}
                </div>
              </li>
            ))}
            {mutation.isPending ? (
              <li className="flex items-center gap-2 text-xs text-foreground-muted">
                <SpinnerIcon size="sm" />
                {t('onboarding.playground.thinking')}
              </li>
            ) : null}
          </ol>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <div className="flex-1">
          <Input
            id={`agent-playground-message-${agentId}`}
            type="text"
            label={t('onboarding.playground.inputLabel')}
            placeholder={t('onboarding.playground.inputPlaceholder')}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
            }}
          />
        </div>
        <Button
          type="submit"
          loading={mutation.isPending}
          disabled={draft.trim().length === 0}
          aria-label={t('onboarding.playground.send')}
        >
          <Send aria-hidden="true" className="h-4 w-4" />
        </Button>
      </form>
    </div>
  )
}
